import React, { useCallback, useEffect, useState } from "react";
import BreadCrumbs from "../../Components/Other/breadcrumbs";
import "./catalogue.css";
import {
  viewExcel,
  assignCatalogue,
} from "../../Redux/Actions/franchiseAction";
import { ReactComponent as TowerIcon } from "../../Global Icons/CatalogueIcon/towers.svg";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { trimKey } from "../../Utils/helpers";

const PlotCatalogue = () => {
  const { category_id, project_type, project_id } = useParams();
  const { enabled, enableError } = useSelector((state) => state.franchises);
  const { projectCatalogueExcel: exceldata } = useSelector(
    (state) => state.franchises
  );

  const {
    selectedBuilderId: builderId,
    selectedFranchiseId: franchiseId,
    selectedBrandName: brandName,
  } = useSelector((state) => state.franchises);

  const dispatch = useDispatch();

  const [towerID, setTowerID] = useState("");
  const [towerData, setTowerData] = useState();
  const [floors, setFloors] = useState([]);
  const [checked, setChecked] = useState(false);
  const [unitData, setUnitData] = useState();
  const [update, setUpdate] = useState(false);

  // State to store selected units
  const [units, setUnits] = useState([]);

  // Helper functions to get min and max floor
  function getMinFloor(arr) {
    if (!Array.isArray(arr) || arr.length === 0) {
      return null;
    }
    return Math.min(...arr);
  }

  function getMaxFloor(arr) {
    if (!Array.isArray(arr) || arr.length === 0) {
      return null;
    }
    return Math.max(...arr);
  }

  // Transform catalogue data column-wise
  const transformToColumnWise = useCallback((floorDataArray) => {
    const unitMap = new Map();
    const unitSlots = Array.from({ length: 400 }, (_, i) => i + 1);

    for (const floorData of floorDataArray) {
      const rawFloor = floorData.lane;
      const currentFloor = rawFloor;

      for (const [key, unit] of Object.entries(floorData)) {
        if (!/^\d+$/.test(key)) continue;

        const unitIndex = parseInt(key);
        const position = unitSlots[unitIndex] || `Slot${unitIndex}`;

        if (typeof unit.unitno !== "string") continue;

        const id = unit.unitno;
        const area = parseInt(unit["area(sq.ft)"]) || 0;
        const status = unit.status || "filtered";

        if (!unitMap.has(id)) {
          unitMap.set(id, {
            id,
            label: `${area} sq ft`,
            position,
            startFloor: currentFloor,
            endFloor: currentFloor,
            floor: currentFloor,
            status,
            floors: [currentFloor],
            lane: rawFloor,
            tempData: unit,
          });
        } else {
          const existing = unitMap.get(id);
          existing.floors.push(currentFloor);
          existing.startFloor = getMinFloor(existing.floors);
          existing.endFloor = getMaxFloor(existing.floors);
        }
      }
    }
    return Array.from(unitMap.values()).map(({ floors, ...rest }) => rest);
  }, []);

  // Render catalogue for selected tower
  const renderCatalogue = useCallback(
    (item) => {
      if (!item || !exceldata?.data?.result) return;

      setTowerID(item._id);

      const matchedData = exceldata.data.result.find(
        (config) => config._id === item._id
      );

      if (!matchedData?.catalogue) return;

      setChecked(matchedData?.status === "active");

      const transformedData = transformToColumnWise(matchedData.catalogue);
      const formattedFloors = matchedData.catalogue.map((i) => i.lane);

      setFloors(formattedFloors);
      setTowerData(transformedData);

      setUnits([]); // Reset selected units on tower change
    },
    [exceldata, transformToColumnWise]
  );

  const getMaxPosition = (arr) =>
    Array.isArray(arr) ? Math.max(...arr.map((item) => item.position)) : 0;

  const maxPosition = getMaxPosition(towerData);

  const getMaxFloorNumber = (arr) =>
    Array.isArray(arr) ? Math.max(...arr.map((item) => item)) : 0;

  const maxFloor = getMaxFloorNumber(floors);

  useEffect(() => {
    if (enabled) {
      toast.success("Tower Status Changed Successfully!");
      return;
    } else if (enableError) {
      toast.error("Error changing status of this tower");
      return;
    }
  }, [enabled, enableError]);

  // Fetch first
  useEffect(() => {
    dispatch(viewExcel(project_id, category_id, project_type));
    return () => {
      dispatch({ type: "RESET_EXCEL" });
    };
  }, [category_id, project_type, dispatch, project_id]);

  // Set first tower as default on page load
  useEffect(() => {
    if (exceldata) {
      renderCatalogue(exceldata?.data?.sites?.[0]);
    }
  }, [exceldata, renderCatalogue]);

  // Disable scroll when modal is opened
  useEffect(() => {
    if (unitData) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [unitData]);

  // Handle unit click - toggle select/deselect and store in units array
  const handleUnitClick = (unit) => {
    if (unit.tempData.is_allotted === true) return; // Prevent selection if already allotted

    const unitStatus = unit.tempData.status || unit.status || "";

    if (unitStatus !== "avl") {
      toast.error("This unit cannot be selected because it is unavailable");
      return;
    }

    const newUnitObj = {
      catalogue: towerID, // ObjectId
      unit: unit.tempData.type || "",
      catalogue_unit_id: unit.tempData.catalogue_unit_id,
      unitno: unit.id,
      index: unit.tempData.index || "",
      floor: unit.startFloor || 0,
      lane: unit.lane || 0,
    };

    const isSelected = units.some(
      (u) => u.catalogue_unit_id === newUnitObj.catalogue_unit_id
    );

    if (!isSelected) {
      setUnits((prev) => [...prev, newUnitObj]);
    } else {
      setUnits((prev) =>
        prev.filter((u) => u.catalogue_unit_id !== newUnitObj.catalogue_unit_id)
      );
    }
  };

  useEffect(() => {
    console.log("Selected units array:", units);
  }, [units]);

  // ----------- Assign Catalogue Button and API --------------
  const handleAssignCatalogue = () => {
    if (units.length === 0) {
      toast.error("Please select at least one unit before assigning.");
      return;
    }

    const payload = {
      franchise_id: franchiseId,
      builder_id: builderId,
      project_id: project_id,
      catalogue_id: towerID,
      units: units.map((u) => ({
        unit: u.unit,
        catalogue_unit_id: u.catalogue_unit_id,
        index: u.index || "",
        unitno: Number(u.unitno) || 0,
        // floor: u.floor,
        lane: u.lane,
        is_allotted: true,
      })),
    };

    dispatch(assignCatalogue(payload))
      .then(() => {
        toast.success("Catalogue assigned successfully!");
        setUnits([]); 
        dispatch(viewExcel(project_id, category_id, project_type));
      })
      .catch((err) => {
        toast.error("Error assigning catalogue: " + (err?.message || "Failed"));
      });
  };

  const handleSelectAllCatalogue = () => {
    if (!towerData || !units) return;

    // Find all units with status "avl" and NOT already allotted
    const availableUnits = towerData.filter(
      (unit) => unit.status === "avl" && unit.tempData.is_allotted !== true
    );

    // Map available units to the same format as units state
    const newSelectedUnits = availableUnits.map((unit) => ({
      catalogue: towerID,
      unit: unit.tempData.type || "",
      catalogue_unit_id: unit.tempData.catalogue_unit_id,
      unitno: unit.id,
      index: unit.tempData.index || "",
      floor: unit.startFloor || 0,
      lane: unit.lane || 0,
      is_allotted: true
    }));

    // Set the selected units state to include all available units
    setUnits(newSelectedUnits);
  };

  // ----------------------------------------------------------

  return (
    <div className="catalogue-view-page-wrapper">
      <div className="catalogue-view-page-header flex-container flex-align-center flex-justify-between">
        <div className="flex-container flex-align-center">
          <BreadCrumbs
            title="Project Details"
            routeName1="Project List"
            routeName2="Project View"
            route1="/projects"
          />
          <div className="catalogue-view-page-header-right gap-10 flex-container flex-align-center">
            {exceldata?.data?.sites?.map((item, index) => (
              <button
                className={`flex-container flex-align-center ${
                  towerID === item._id ? "active-catalogue" : ""
                }`}
                id={`tower-btn-${index}`}
                key={item._id}
                onClick={() => renderCatalogue(item)}
              >
                <TowerIcon />
                <span>{item.site_name}</span>
              </button>
            ))}
          </div>
          <div style={{ marginBottom: "1rem", textAlign: "right" }}>
            <button
              className="assign-catalogue-btn"
              onClick={handleAssignCatalogue}
            >
              Assign Catalogue
            </button>
            <button
              className="select-all-catalogue-btn"
              onClick={handleSelectAllCatalogue}
            >
              Select All Catalogue
            </button>
          </div>
        </div>
      </div>

      {towerData && (
        <div className="catalogue-view-container flex-container">
          <div className="catalogue-view-floors gap-5 border-8 flex-container flex-align-center">
            {floors.map((item) => (
              <div className="catalogue-floor-tile mirror border-8" key={item}>
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div
            className="mirror catalogue-map-container gap-5 catalogue-commercial-map-container grid-container"
            style={{
              gridTemplateColumns: `repeat(${maxPosition}, 1fr)`,
              gridTemplateRows: `repeat(${maxFloor}, 74px)`,
              transform: "scaleY(-1)",
            }}
          >
            {towerData.map((unit, index) =>
              trimKey(unit.tempData.type) === "road" ||
              trimKey(unit.tempData.type.split("-")[0]) === "road" ? (
                <div
                  key={unit.id}
                  style={{
                    gridColumn: unit.position,
                    gridRow: `${unit.startFloor} / ${unit.endFloor + 1}`,
                    transform: "scaleY(-1)",
                  }}
                  className="road flex-container flex-center"
                >
                  <div
                    className="lane center-line"
                    style={{
                      transform:
                        (trimKey(towerData[index - 1]?.tempData.type) !==
                          "road" ||
                          !trimKey(towerData[index - 1]?.tempData.type)) &&
                        (trimKey(towerData[index + 1]?.tempData.type) !==
                          "road" ||
                          !trimKey(towerData[index + 1]?.tempData.type))
                          ? "rotate(90deg)"
                          : "",
                    }}
                  ></div>
                  {towerData[index - 1]?.tempData.type !== "road" &&
                  towerData[index + 1]?.tempData.type !== "road" ? null : (
                    <p className="road-name road-bg">
                      {unit.tempData.type.split("-")[0]}
                    </p>
                  )}

                  <div
                    className="lane side-line left"
                    style={{
                      visibility:
                        (trimKey(towerData[index - 1]?.tempData.type) !==
                          "road" ||
                          !trimKey(towerData[index - 1]?.tempData.type)) &&
                        (trimKey(towerData[index + 1]?.tempData.type) !==
                          "road" ||
                          !trimKey(towerData[index + 1]?.tempData.type))
                          ? "hidden"
                          : "visible",
                    }}
                  ></div>
                  <div
                    className="lane side-line right"
                    style={{
                      visibility:
                        (trimKey(towerData[index - 1]?.tempData.type) !==
                          "road" ||
                          !trimKey(towerData[index - 1]?.tempData.type)) &&
                        (trimKey(towerData[index + 1]?.tempData.type) !==
                          "road" ||
                          !trimKey(towerData[index + 1]?.tempData.type))
                          ? "hidden"
                          : "visible",
                    }}
                  ></div>

                  <div
                    className="lane top-line top"
                    style={{
                      visibility:
                        (trimKey(towerData[index - 1]?.tempData.type) !==
                          "road" ||
                          !trimKey(towerData[index - 1]?.tempData.type)) &&
                        (trimKey(towerData[index + 1]?.tempData.type) !==
                          "road" ||
                          !trimKey(towerData[index + 1]?.tempData.type))
                          ? "visible"
                          : "hidden",
                    }}
                  ></div>
                  <div
                    className="lane top-line bottom"
                    style={{
                      visibility:
                        (trimKey(towerData[index - 1]?.tempData.type) !==
                          "road" ||
                          !trimKey(towerData[index - 1]?.tempData.type)) &&
                        (trimKey(towerData[index + 1]?.tempData.type) !==
                          "road" ||
                          !trimKey(towerData[index + 1]?.tempData.type))
                          ? "visible"
                          : "hidden",
                    }}
                  ></div>
                </div>
              ) : trimKey(unit.tempData.type) === "park" ||
                trimKey(unit.tempData.type.split("-")[0]) === "park" ? (
                <div
                  className="park upgraded-park flex-container flex-center"
                  key={unit.id}
                  style={{
                    gridColumn: unit.position,
                    gridRow: `${unit.startFloor} / ${unit.endFloor + 1}`,
                    transform: "scaleY(-1)",
                  }}
                >
                  <p className="road-name">
                    {unit.tempData.type.split("-")[0]}
                  </p>

                  <div className="grass-texture"></div>
                  <div className="bench"></div>
                </div>
              ) : (
                <div
                  className={`mirror plot-card catalogue-unit-tile-main commercial-catalogue-unit-tile-main gap-5 border-8 flex-container flex-align-center`}
                  key={unit.id}
                  style={{
                    gridColumn: unit.position,
                    gridRow: `${unit.startFloor} / ${unit.endFloor + 1}`,
                    transform: "scaleY(-1)",
                    border:
                      unit.tempData.is_allotted === true
                        ? "2px solid green"
                        : units.some(
                            (u) =>
                              u.catalogue_unit_id ===
                              unit.tempData.catalogue_unit_id
                          )
                        ? "2px solid green"
                        : unit.tempData.status === "unavl"
                        ? "2px solid rgb(230, 73, 0)"
                        : unit.status === "avl"
                        ? "2px solid rgb(6, 170, 0)"
                        : unit.status === "onhold"
                        ? "2px solid rgb(198, 205, 0)"
                        : unit.status === "sold"
                        ? "2px solid rgb(189, 0, 0)"
                        : "none",
                    cursor:
                      unit.tempData.is_allotted === true ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (unit.tempData.is_allotted === true) return;
                    handleUnitClick(unit);
                  }}
                >
                  <div
                    className="catalogue-tile-unit-no mirror flex-container flex-center"
                    style={{
                      backgroundColor:
                        unit.tempData.is_allotted === true
                          ? "green"
                          : units.some(
                              (u) =>
                                u.catalogue_unit_id ===
                                unit.tempData.catalogue_unit_id
                            )
                          ? "green"
                          : "#686868",
                      border: "none",
                    }}
                  >
                    <span title={unit.tempData.unitno}>
                      {unit.tempData.unitno}
                    </span>
                  </div>
                  <div className="catalogue-tile-unit-type flex-container flex-column plot-tile">
                    <span
                      style={{ textTransform: "capitalize" }}
                    >{`${unit.label}`}</span>
                    {trimKey(unit.tempData.corner) === "yes" && (
                      <span className="plot-tile-type">Corner</span>
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}
      {/* Modal to show the data on click of any particular unit */}
    </div>
  );
};

export default PlotCatalogue;
